import {
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  type DataStrategyFunction,
  type DataStrategyResult,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { HydrogenLayout, MenuItemsProvider, SessionProvider, ErrorSurface, GlobalDrawer, GlobalModal } from "@steelyard/theme";
import { endSession, getAccount, getPermissionState, safeReturnTo, signOut } from "../lib/identity";
import { menuItemsFor, permissionForPath } from "../lib/nav";
import { listReturn } from "../lib/navigation";
import { can } from "../lib/permissions";
import { NotificationsHost } from "../components/notifications-host";
import type { PermissionState } from "../types";

/**
 * 017: structure copied from vendor/isomorphic/apps/isomorphic/src/app/(hydrogen)/layout.tsx (the
 * Next route shell that selects a layout) + vendor/isomorphic/apps/isomorphic/src/layouts/hydrogen/
 * layout.tsx — copied verbatim into `packages/theme/src/layouts/hydrogen/layout.tsx` and exported
 * as `HydrogenLayout`, the template's own file, not a version of it.
 *
 * What this route supplies is the two things a route-agnostic shell cannot know for itself: the nav
 * (`MenuItemsProvider`, filtered by the live permission set) and the signed-in account
 * (`SessionProvider`, which is what replaced `next-auth` for the copied `ProfileMenu`) — then gates
 * or renders the matched child.
 *
 * `018` deleted `components/layout/hydrogen/*` — ten files that re-drew the template's sidebar,
 * header, profile menu and sticky header by hand.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */

/**
 * Ported from apps/admin/app/(app)/layout.tsx — same three things, same order, before any screen
 * renders: read the account (a revoked session still carries its cookies, so their presence
 * decides nothing), read the permission set, gate the route. This loader does the two reads; the
 * gate is `refusedAt`, applied by `RootRoute` and `guardedDataStrategy` against the current URL,
 * because this loader runs on a full load but not on every in-app navigation.
 */
export async function rootLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const path = url.pathname;
  // Query string included in returnTo (e.g. "?page=3") — dropping it silently loses where the
  // person actually was, not just which route (apps/admin/e2e/auth.spec.ts's original assertion).
  const pathWithQuery = path + url.search;

  const account = await getAccount();
  if (!account) throw redirect(`/sign-in?returnTo=${encodeURIComponent(safeReturnTo(pathWithQuery))}`);

  const permissionState = await getPermissionState();
  // Spec §5 "Surface": customers, packers and supervisors are not back-office users. The BFF already
  // refuses their sign-in; this covers a session that got here another way — one that predates the
  // check, a matrix edited after sign-in, or the customer portal's session (both apps share the
  // `sy_at`/`sy_rt` cookies on one host). Such a session is ended and sent back to sign-in with the
  // reason, never shown the back office's shell as if it were signed in.
  if (!permissionState.permissions.has("backoffice.access")) {
    await endSession();
    throw redirect("/sign-in?denied=backoffice");
  }
  lastPermissionState = permissionState;

  return { account, permissionState };
}

/** The route id `main.tsx` gives the shell route, so `guardedDataStrategy` can find its loader. */
export const ROOT_ROUTE_ID = "root";

/**
 * The live permission set the root loader last read. React Router does not re-run a parent's loader
 * when only a child route changes, so the gate below cannot live in `rootLoader`'s return value —
 * it would keep the first full page load's answer for every later in-app navigation (refusing an
 * allowed screen after a refused one, and opening a refused screen reached by a link).
 */
let lastPermissionState: PermissionState | undefined;

/**
 * For a child loader that must skip a read its caller cannot make. `guardedDataStrategy` runs the
 * root loader first, so inside the shell this is always set.
 */
export function loaderPermissions(): PermissionState {
  if (!lastPermissionState) throw new Error("loaderPermissions() called before the root loader ran.");
  return lastPermissionState;
}

/** The screen's own permission, from `nav.tsx`, against the live set. */
export function refusedAt(pathname: string, permissionState: PermissionState): boolean {
  const required = permissionForPath(pathname);
  return required !== undefined && !permissionState.permissions.has(required);
}

/**
 * The router's `dataStrategy`: runs the root loader (when it runs at all) BEFORE the child loaders,
 * then skips a refused screen's own loaders — otherwise they fire anyway and their 403s surface
 * as console noise or an uncaught error behind the "Not permitted" panel. Navigations only: an
 * action or a fetcher goes through untouched, and the API still refuses it on its own.
 */
export const guardedDataStrategy: DataStrategyFunction = async ({ request, matches, fetcherKey }) => {
  const toLoad = matches.filter((match) => match.shouldLoad);
  const results: Record<string, DataStrategyResult> = {};

  // Back to a list from one of its own records: the list as it was left (`listReturn`), before its
  // loader reads the unfiltered set for nothing.
  const back = fetcherKey === null && request.method === "GET" ? listReturn(new URL(request.url)) : undefined;
  if (back) return Object.fromEntries(toLoad.map((match) => [match.route.id, { type: "data", result: redirect(back) }]));

  const root = toLoad.find((match) => match.route.id === ROOT_ROUTE_ID);
  if (root) results[root.route.id] = await root.resolve();
  // A root redirect (sign-in) or failure: nothing below it should load for this session.
  const rootFailed = root !== undefined && (results[root.route.id].type === "error" || results[root.route.id].result instanceof Response);

  const refused =
    rootFailed ||
    (fetcherKey === null &&
      request.method === "GET" &&
      lastPermissionState !== undefined &&
      refusedAt(new URL(request.url).pathname, lastPermissionState));

  await Promise.all(
    toLoad
      .filter((match) => match !== root)
      .map(async (match) => {
        results[match.route.id] = await (refused ? match.resolve(() => null) : match.resolve());
      }),
  );
  return results;
};

/**
 * react-router-dom's `useLoaderData`/`useOutletContext` are untyped in library mode (no
 * codegen'd route types, since this app uses plain `createBrowserRouter` rather than the
 * framework-mode route config) — this is the documented pattern for that: a seam type derived
 * from the loader's own return type, cast once here and again at `useOutletContext` call sites.
 */
export type RootLoaderData = Awaited<ReturnType<typeof rootLoader>>;

export function RootRoute() {
  const { account, permissionState } = useLoaderData() as RootLoaderData;
  // Decided from the current URL on every render, not from the loader's last run (see above).
  const refused = refusedAt(useLocation().pathname, permissionState);

  return (
    <SessionProvider
      value={{
        user: {
          name: account.displayName ?? account.email ?? "Unknown user",
          email: account.email ?? undefined,
          role: account.role,
          matrixRole: permissionState.role,
        },
        signOut,
        mayReadAudit: can(permissionState, "identity.audit.read"),
      }}
    >
      <MenuItemsProvider items={menuItemsFor(permissionState.permissions)}>
        <NotificationsHost>
        <HydrogenLayout>
          {refused ? (
            <ErrorSurface
              error={{
                title: "Not permitted",
                status: 403,
                detail:
                  "This screen is gated by a permission your role does not hold. Ask an administrator to change the role matrix if you need it.",
              }}
              action="open this screen"
            />
          ) : (
            <Outlet context={{ account, permissionState }} />
          )}
        </HydrogenLayout>
        {/* Rendered here, not in main.tsx's RootLayout: a drawer or modal view (the phone nav, the
            header search) reads the menu, session and notification contexts, and above RootRoute
            those were empty — the phone nav drawer opened with no menu in it. */}
        <GlobalDrawer />
        <GlobalModal />
        </NotificationsHost>
      </MenuItemsProvider>
    </SessionProvider>
  );
}
